<template>
  <div class="statement-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Back Button & Title -->
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            class="back-btn mr-3"
            @click="goBack"
            title="Go Back"
          >
            <v-icon color="#0f172a" size="20">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-icon-box mr-3">
            <v-icon color="#9a6b27" size="24">mdi-book-open-page-variant-outline</v-icon>
          </div>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <span class="page-title">OLD GOLD STATEMENT &amp; RECONCILIATION LEDGER</span>
              <span class="badge-statement-date">
                {{ formatDateDisplay(fromDate) }} &rarr; {{ formatDateDisplay(toDate) }}
              </span>
            </div>
            <div class="page-subtitle">
              Comprehensive intake purchases vs. refining issue ledger &amp; stock balance statement
            </div>
          </div>
        </div>

        <!-- Right: Actions (Refresh, CSV, PDF) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-btn
            icon
            small
            title="Refresh Ledger"
            class="refresh-icon-btn"
            :loading="appLoading"
            @click="getList"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            outlined
            color="#475569"
            class="btn-secondary text-capitalize"
            @click="exportStatementCSV"
            :disabled="purchaseRows.length === 0 && issueRows.length === 0"
          >
            <v-icon left small color="#475569">mdi-file-delimited-outline</v-icon>
            Export CSV
          </v-btn>

          <v-btn
            outlined
            color="#9a6b27"
            class="btn-gold-outline text-capitalize"
            @click="printStatement"
          >
            <v-icon left small color="#9a6b27">mdi-printer-outline</v-icon>
            Print
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            :loading="pdfLoading"
            @click="downloadStatement"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 Reconciliation KPI Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Inward Purchases -->
      <div class="kpi-card inward-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL INWARD (PURCHASES)</div>
          <div class="kpi-value text-emerald">
            {{ Number(purchaseTotals.netWeight || 0).toFixed(3) }} <span class="unit-text">gm</span>
          </div>
          <div class="kpi-hint">
            <span>Valuation: <b>{{ formatCurrency(purchaseTotals.amount || 0) }}</b></span>
            <span class="mx-1">&bull;</span>
            <span>{{ filteredPurchaseRows.length }} bills</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #15803d;">
          <v-icon size="22" color="#15803d">mdi-arrow-bottom-left</v-icon>
        </div>
      </div>

      <!-- 2. Total Outward Issues -->
      <div class="kpi-card outward-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL OUTWARD (ISSUED)</div>
          <div class="kpi-value text-amber">
            {{ Number(issueTotals.netWeight || 0).toFixed(3) }} <span class="unit-text">gm</span>
          </div>
          <div class="kpi-hint">
            <span>Valuation: <b>{{ formatCurrency(issueTotals.amount || 0) }}</b></span>
            <span class="mx-1">&bull;</span>
            <span>{{ filteredIssueRows.length }} issues</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #b45309;">
          <v-icon size="22" color="#b45309">mdi-arrow-top-right</v-icon>
        </div>
      </div>

      <!-- 3. Net Holding Balance (Inward - Outward) -->
      <div class="kpi-card balance-card">
        <div class="kpi-content">
          <div class="kpi-label">NET BALANCE (STOCK IN HAND)</div>
          <div class="kpi-value gold-val">
            {{ netBalanceWeight.toFixed(3) }} <span class="unit-text">gm</span>
          </div>
          <div class="kpi-hint">
            <span>Holding Value: <b>{{ formatCurrency(netBalanceAmount) }}</b></span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="22" color="#2563eb">mdi-scale-balance</v-icon>
        </div>
      </div>

      <!-- 4. Total Melt & Less Deductions -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL LESS / MELT DEDUCTIONS</div>
          <div class="kpi-value text-slate-800">
            {{ Number(purchaseTotals.less || 0).toFixed(3) }} <span class="unit-text">gm</span>
          </div>
          <div class="kpi-hint">
            <span>Stone Wt: <b>{{ Number(purchaseTotals.stoneWeight || 0).toFixed(3) }}g</b></span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #faf5ff; color: #7c3aed;">
          <v-icon size="22" color="#7c3aed">mdi-fire</v-icon>
        </div>
      </div>
    </div>

    <!-- Date Filter & Search Toolbar Card -->
    <v-card flat class="filter-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
        <!-- Left: Search Box -->
        <div class="search-input-wrap">
          <v-text-field
            v-model="searchKeyword"
            background-color="#f8fafc"
            color="#9a6b27"
            clearable
            hide-details
            placeholder="Filter by Bill No or Date..."
            outlined
            dense
            class="custom-search-field"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>

        <!-- Middle: Date Range Quick Presets -->
        <div class="date-presets-wrap d-flex align-center flex-wrap" style="gap: 6px;">
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'today' ? 'preset-active' : '']"
            @click="setDatePreset('today')"
          >
            Today
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'yesterday' ? 'preset-active' : '']"
            @click="setDatePreset('yesterday')"
          >
            Yesterday
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'week' ? 'preset-active' : '']"
            @click="setDatePreset('week')"
          >
            This Week
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'month' ? 'preset-active' : '']"
            @click="setDatePreset('month')"
          >
            This Month
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'year' ? 'preset-active' : '']"
            @click="setDatePreset('year')"
          >
            This Year
          </v-btn>
        </div>

        <!-- Right: Date Range Pickers (From / To) -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="fromDate"
                label="From Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              no-title
              scrollable
              color="#9a6b27"
              @input="menu = false"
            ></v-date-picker>
          </v-menu>

          <span class="text-muted" style="font-size: 12px;">to</span>

          <v-menu
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="toDate"
                label="To Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              no-title
              scrollable
              color="#9a6b27"
              @input="menu2 = false"
            ></v-date-picker>
          </v-menu>
        </div>
      </div>
    </v-card>

    <!-- View Mode Selector Tabs -->
    <div class="view-tabs-bar mb-4 d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
      <div class="d-flex align-center" style="gap: 8px;">
        <v-btn
          small
          depressed
          :class="['tab-toggle-btn', activeView === 'both' ? 'tab-active' : '']"
          @click="activeView = 'both'"
        >
          <v-icon x-small left>mdi-view-agenda-outline</v-icon>
          Split Ledger View
        </v-btn>
        <v-btn
          small
          depressed
          :class="['tab-toggle-btn', activeView === 'purchase' ? 'tab-active' : '']"
          @click="activeView = 'purchase'"
        >
          <v-icon x-small left color="#15803d">mdi-arrow-bottom-left</v-icon>
          Inward Purchases ({{ filteredPurchaseRows.length }})
        </v-btn>
        <v-btn
          small
          depressed
          :class="['tab-toggle-btn', activeView === 'issue' ? 'tab-active' : '']"
          @click="activeView = 'issue'"
        >
          <v-icon x-small left color="#b45309">mdi-arrow-top-right</v-icon>
          Outward Issues ({{ filteredIssueRows.length }})
        </v-btn>
      </div>

      <div class="statement-date-tag">
        <v-icon x-small color="#64748b" class="mr-1">mdi-calendar-clock</v-icon>
        <span>Period: <b>{{ fromDate }}</b> to <b>{{ toDate }}</b></span>
      </div>
    </div>

    <!-- Section 1: OLD GOLD PURCHASE (INWARD) -->
    <v-card
      v-if="activeView === 'both' || activeView === 'purchase'"
      flat
      class="table-section-card rounded-lg mb-4 overflow-hidden"
    >
      <div class="section-header-bar px-4 py-3 d-flex align-center justify-space-between">
        <div class="d-flex align-center" style="gap: 8px;">
          <div class="section-icon-box inward-bg">
            <v-icon size="16" color="#15803d">mdi-arrow-bottom-left</v-icon>
          </div>
          <span class="section-title">OLD GOLD PURCHASE (INWARD REGISTER)</span>
          <span class="count-pill inward-pill">{{ filteredPurchaseRows.length }} Entries</span>
        </div>
        <div class="text-caption text-muted">
          Customer old gold receipts recorded in sales
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredPurchaseRows"
        :loading="appLoading"
        hide-default-footer
        disable-pagination
        class="pro-statement-table elevation-0"
      >
        <!-- Bill No -->
        <template #item.billNo="{ item }">
          <span class="bill-badge">{{ item.billNo || 'N/A' }}</span>
        </template>

        <!-- Date -->
        <template #item.date="{ item }">
          <span class="date-text">{{ item.date }}</span>
        </template>

        <!-- Gross Weight -->
        <template #item.grossWeight="{ item }">
          <span class="text-slate-700">{{ Number(item.grossWeight || 0).toFixed(3) }}</span>
        </template>

        <!-- Stone Weight -->
        <template #item.stoneWeight="{ item }">
          <span class="text-muted">{{ Number(item.stoneWeight || 0).toFixed(3) }}</span>
        </template>

        <!-- Less / Melt -->
        <template #item.less="{ item }">
          <span class="less-badge">{{ Number(item.less || 0).toFixed(3) }}</span>
        </template>

        <!-- Net Weight -->
        <template #item.netWeight="{ item }">
          <span class="net-weight-badge font-weight-bold">
            {{ Number(item.netWeight || 0).toFixed(3) }} g
          </span>
        </template>

        <!-- Amount -->
        <template #item.amount="{ item }">
          <span class="amount-text font-weight-bold text-emerald">
            {{ formatCurrency(item.amount || 0) }}
          </span>
        </template>

        <!-- Total Footer Row -->
        <template v-slot:body.append>
          <tr class="table-total-row">
            <td class="text-left font-weight-bold" colspan="2">
              <div class="d-flex align-center" style="gap: 6px;">
                <v-icon small color="#15803d">mdi-sigma</v-icon>
                <span>TOTAL INWARD PURCHASES</span>
              </div>
            </td>
            <td class="text-right font-weight-bold text-slate-800">{{ computedPurchaseTotals.grossWeight.toFixed(3) }}</td>
            <td class="text-right font-weight-bold text-muted">{{ computedPurchaseTotals.stoneWeight.toFixed(3) }}</td>
            <td class="text-right font-weight-bold text-amber">{{ computedPurchaseTotals.less.toFixed(3) }}</td>
            <td class="text-right font-weight-bold text-gold-total">{{ computedPurchaseTotals.netWeight.toFixed(3) }} g</td>
            <td class="text-right font-weight-bold text-emerald-total">{{ formatCurrency(computedPurchaseTotals.amount) }}</td>
          </tr>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="empty-table-state py-6 text-center">
            <v-icon size="28" color="#94a3b8" class="mb-1">mdi-tray-arrow-down</v-icon>
            <div class="empty-table-text">No old gold purchases recorded for this period</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Section 2: OLD GOLD ISSUE (OUTWARD) -->
    <v-card
      v-if="activeView === 'both' || activeView === 'issue'"
      flat
      class="table-section-card rounded-lg mb-4 overflow-hidden"
    >
      <div class="section-header-bar px-4 py-3 d-flex align-center justify-space-between">
        <div class="d-flex align-center" style="gap: 8px;">
          <div class="section-icon-box outward-bg">
            <v-icon size="16" color="#b45309">mdi-arrow-top-right</v-icon>
          </div>
          <span class="section-title">OLD GOLD ISSUE (OUTWARD / ACIDIFYING REGISTER)</span>
          <span class="count-pill outward-pill">{{ filteredIssueRows.length }} Entries</span>
        </div>
        <div class="text-caption text-muted">
          Old gold batches dispatched for refining / melt testing
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredIssueRows"
        :loading="appLoading"
        hide-default-footer
        disable-pagination
        class="pro-statement-table elevation-0"
      >
        <!-- Bill No -->
        <template #item.billNo="{ item }">
          <span class="bill-badge issue-badge">{{ item.billNo || 'N/A' }}</span>
        </template>

        <!-- Date -->
        <template #item.date="{ item }">
          <span class="date-text">{{ item.date }}</span>
        </template>

        <!-- Gross Weight -->
        <template #item.grossWeight="{ item }">
          <span class="text-slate-700">{{ Number(item.grossWeight || 0).toFixed(3) }}</span>
        </template>

        <!-- Stone Weight -->
        <template #item.stoneWeight="{ item }">
          <span class="text-muted">{{ Number(item.stoneWeight || 0).toFixed(3) }}</span>
        </template>

        <!-- Less / Melt -->
        <template #item.less="{ item }">
          <span class="less-badge">{{ Number(item.less || 0).toFixed(3) }}</span>
        </template>

        <!-- Net Weight -->
        <template #item.netWeight="{ item }">
          <span class="net-weight-badge font-weight-bold">
            {{ Number(item.netWeight || 0).toFixed(3) }} g
          </span>
        </template>

        <!-- Amount -->
        <template #item.amount="{ item }">
          <span class="amount-text font-weight-bold text-amber">
            {{ formatCurrency(item.amount || 0) }}
          </span>
        </template>

        <!-- Total Footer Row -->
        <template v-slot:body.append>
          <tr class="table-total-row">
            <td class="text-left font-weight-bold" colspan="2">
              <div class="d-flex align-center" style="gap: 6px;">
                <v-icon small color="#b45309">mdi-sigma</v-icon>
                <span>TOTAL OUTWARD ISSUES</span>
              </div>
            </td>
            <td class="text-right font-weight-bold text-slate-800">{{ computedIssueTotals.grossWeight.toFixed(3) }}</td>
            <td class="text-right font-weight-bold text-muted">{{ computedIssueTotals.stoneWeight.toFixed(3) }}</td>
            <td class="text-right font-weight-bold text-amber">{{ computedIssueTotals.less.toFixed(3) }}</td>
            <td class="text-right font-weight-bold text-gold-total">{{ computedIssueTotals.netWeight.toFixed(3) }} g</td>
            <td class="text-right font-weight-bold text-amber-total">{{ formatCurrency(computedIssueTotals.amount) }}</td>
          </tr>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="empty-table-state py-6 text-center">
            <v-icon size="28" color="#94a3b8" class="mb-1">mdi-tray-arrow-up</v-icon>
            <div class="empty-table-text">No old gold issue batches recorded for this period</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Reconciliation Summary Banner Card -->
    <v-card flat class="reconciliation-summary-card pa-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <div class="d-flex align-center">
          <div class="recon-icon-box mr-3">
            <v-icon color="#2563eb" size="24">mdi-scale-balance</v-icon>
          </div>
          <div>
            <div class="recon-title">RECONCILIATION SUMMARY (BALANCE)</div>
            <div class="recon-sub">Net stock difference between inward receipts and refining issues</div>
          </div>
        </div>

        <div class="recon-metrics-wrap d-flex align-center flex-wrap" style="gap: 24px;">
          <div class="recon-stat">
            <span class="recon-label">GROSS BALANCE:</span>
            <span class="recon-val">{{ (computedPurchaseTotals.grossWeight - computedIssueTotals.grossWeight).toFixed(3) }} g</span>
          </div>
          <div class="recon-stat">
            <span class="recon-label">NET GOLD BALANCE:</span>
            <span class="recon-val gold-val">{{ (computedPurchaseTotals.netWeight - computedIssueTotals.netWeight).toFixed(3) }} g</span>
          </div>
          <div class="recon-stat">
            <span class="recon-label">VALUATION BALANCE:</span>
            <span class="recon-val text-emerald">{{ formatCurrency(computedPurchaseTotals.amount - computedIssueTotals.amount) }}</span>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "OldGoldStatement",
  data() {
    const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10);

    return {
      appLoading: false,
      pdfLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,

      menu: false,
      menu2: false,
      fromDate: today,
      toDate: today,
      datePreset: "today",

      searchKeyword: "",
      activeView: "both", // 'both', 'purchase', 'issue'

      purchaseRows: [],
      issueRows: [],
      purchaseTotals: { grossWeight: 0, stoneWeight: 0, less: 0, netWeight: 0, amount: 0 },
      issueTotals: { grossWeight: 0, stoneWeight: 0, less: 0, netWeight: 0, amount: 0 },

      headers: [
        { text: "DATE", value: "date", sortable: true, align: "left", width: "120px" },
        { text: "BILL NO.", value: "billNo", sortable: true, align: "left", width: "130px" },
        { text: "GROSS WT (g)", value: "grossWeight", sortable: true, align: "right", width: "130px" },
        { text: "STONE WT (g)", value: "stoneWeight", sortable: true, align: "right", width: "130px" },
        { text: "LESS / MELT (g)", value: "less", sortable: true, align: "right", width: "130px" },
        { text: "NET WEIGHT (g)", value: "netWeight", sortable: true, align: "right", width: "140px" },
        { text: "VALUATION (₹)", value: "amount", sortable: true, align: "right", width: "150px" },
      ],
    };
  },
  computed: {
    // Filtered purchases
    filteredPurchaseRows() {
      if (!this.searchKeyword || this.searchKeyword.trim() === "") {
        return this.purchaseRows || [];
      }
      const q = this.searchKeyword.toLowerCase().trim();
      return (this.purchaseRows || []).filter((r) => {
        const b = String(r.billNo || "").toLowerCase();
        const d = String(r.date || "").toLowerCase();
        return b.includes(q) || d.includes(q);
      });
    },

    // Filtered issues
    filteredIssueRows() {
      if (!this.searchKeyword || this.searchKeyword.trim() === "") {
        return this.issueRows || [];
      }
      const q = this.searchKeyword.toLowerCase().trim();
      return (this.issueRows || []).filter((r) => {
        const b = String(r.billNo || "").toLowerCase();
        const d = String(r.date || "").toLowerCase();
        return b.includes(q) || d.includes(q);
      });
    },

    // Computed live purchase totals from filtered view
    computedPurchaseTotals() {
      const totals = { grossWeight: 0, stoneWeight: 0, less: 0, netWeight: 0, amount: 0 };
      this.filteredPurchaseRows.forEach((r) => {
        totals.grossWeight += Number(r.grossWeight) || 0;
        totals.stoneWeight += Number(r.stoneWeight) || 0;
        totals.less += Number(r.less) || 0;
        totals.netWeight += Number(r.netWeight) || 0;
        totals.amount += Number(r.amount) || 0;
      });
      return totals;
    },

    // Computed live issue totals from filtered view
    computedIssueTotals() {
      const totals = { grossWeight: 0, stoneWeight: 0, less: 0, netWeight: 0, amount: 0 };
      this.filteredIssueRows.forEach((r) => {
        totals.grossWeight += Number(r.grossWeight) || 0;
        totals.stoneWeight += Number(r.stoneWeight) || 0;
        totals.less += Number(r.less) || 0;
        totals.netWeight += Number(r.netWeight) || 0;
        totals.amount += Number(r.amount) || 0;
      });
      return totals;
    },

    // Net holding balance calculations
    netBalanceWeight() {
      return (this.computedPurchaseTotals.netWeight || 0) - (this.computedIssueTotals.netWeight || 0);
    },
    netBalanceAmount() {
      return (this.computedPurchaseTotals.amount || 0) - (this.computedIssueTotals.amount || 0);
    },
  },
  watch: {
    fromDate() {
      this.getList();
    },
    toDate() {
      this.getList();
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push("/oldGoldList");
      }
    },

    formatDateDisplay(dateStr) {
      if (!dateStr) return "-";
      const dt = new Date(dateStr);
      if (isNaN(dt.getTime())) return dateStr;
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },

    formatCurrency(val) {
      const num = Number(val) || 0;
      return "₹ " + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    setDatePreset(preset) {
      this.datePreset = preset;
      const now = new Date();
      const formatDateISO = (d) => {
        return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().substr(0, 10);
      };

      if (preset === "today") {
        this.fromDate = formatDateISO(now);
        this.toDate = formatDateISO(now);
      } else if (preset === "yesterday") {
        const yest = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        this.fromDate = formatDateISO(yest);
        this.toDate = formatDateISO(yest);
      } else if (preset === "week") {
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        this.fromDate = formatDateISO(start);
        this.toDate = formatDateISO(now);
      } else if (preset === "month") {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        this.fromDate = formatDateISO(start);
        this.toDate = formatDateISO(now);
      } else if (preset === "year") {
        const start = new Date(now.getFullYear(), 0, 1);
        this.fromDate = formatDateISO(start);
        this.toDate = formatDateISO(now);
      }
    },

    getList() {
      if (!this.fromDate || !this.toDate) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/oldgoldstatement",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          fromDate: this.fromDate,
          toDate: this.toDate,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.purchaseRows = response.data.data.purchaseRows || [];
            this.issueRows = response.data.data.issueRows || [];
            this.purchaseTotals = response.data.data.purchaseTotals || { grossWeight: 0, stoneWeight: 0, less: 0, netWeight: 0, amount: 0 };
            this.issueTotals = response.data.data.issueTotals || { grossWeight: 0, stoneWeight: 0, less: 0, netWeight: 0, amount: 0 };
          } else {
            this.msg = response.data.msg || "Failed to retrieve statement";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error fetching old gold statement:", err);
        });
    },

    downloadStatement() {
      if (!this.fromDate || !this.toDate) {
        this.msg = "Please choose both dates";
        this.showsnackbar = true;
        return;
      }
      this.pdfLoading = true;
      axios({
        method: "get",
        url: "/download/oldgoldstatement",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          fromDate: this.fromDate,
          toDate: this.toDate,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.pdfLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `OldGoldStatement_${this.fromDate}_to_${this.toDate}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.pdfLoading = false;
          this.ServerError = true;
          console.error("Error downloading PDF statement:", err);
          this.msg = "Failed to download PDF statement";
          this.showsnackbar = true;
        });
    },

    printStatement() {
      window.print();
    },

    exportStatementCSV() {
      const pRows = this.filteredPurchaseRows;
      const iRows = this.filteredIssueRows;

      const lines = [
        `"OLD GOLD STATEMENT & RECONCILIATION LEDGER"`,
        `"Period: ${this.fromDate} to ${this.toDate}"`,
        `""`,
        `"--- 1. OLD GOLD PURCHASE (INWARD REGISTER) ---"`,
        `"Date","Bill No","Gross Wt (g)","Stone Wt (g)","Less / Melt (g)","Net Wt (g)","Valuation (INR)"`
      ];

      pRows.forEach((r) => {
        lines.push(`"${r.date}","${r.billNo}",${Number(r.grossWeight || 0).toFixed(3)},${Number(r.stoneWeight || 0).toFixed(3)},${Number(r.less || 0).toFixed(3)},${Number(r.netWeight || 0).toFixed(3)},${Number(r.amount || 0).toFixed(2)}`);
      });

      lines.push(`"TOTAL INWARD","--",${this.computedPurchaseTotals.grossWeight.toFixed(3)},${this.computedPurchaseTotals.stoneWeight.toFixed(3)},${this.computedPurchaseTotals.less.toFixed(3)},${this.computedPurchaseTotals.netWeight.toFixed(3)},${this.computedPurchaseTotals.amount.toFixed(2)}`);
      lines.push(`""`);
      lines.push(`"--- 2. OLD GOLD ISSUE (OUTWARD / REFINING REGISTER) ---"`);
      lines.push(`"Date","Batch / Bill No","Gross Wt (g)","Stone Wt (g)","Less / Melt (g)","Net Wt (g)","Valuation (INR)"`);

      iRows.forEach((r) => {
        lines.push(`"${r.date}","${r.billNo}",${Number(r.grossWeight || 0).toFixed(3)},${Number(r.stoneWeight || 0).toFixed(3)},${Number(r.less || 0).toFixed(3)},${Number(r.netWeight || 0).toFixed(3)},${Number(r.amount || 0).toFixed(2)}`);
      });

      lines.push(`"TOTAL OUTWARD","--",${this.computedIssueTotals.grossWeight.toFixed(3)},${this.computedIssueTotals.stoneWeight.toFixed(3)},${this.computedIssueTotals.less.toFixed(3)},${this.computedIssueTotals.netWeight.toFixed(3)},${this.computedIssueTotals.amount.toFixed(2)}`);
      lines.push(`""`);
      lines.push(`"--- 3. RECONCILIATION BALANCE ---"`);
      lines.push(`"Net Holding Weight (g)",${this.netBalanceWeight.toFixed(3)}`);
      lines.push(`"Holding Valuation (INR)",${this.netBalanceAmount.toFixed(2)}`);

      const csvContent = "data:text/csv;charset=utf-8," + lines.join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `OldGold_Statement_${this.fromDate}_${this.toDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.statement-page-wrapper {
  padding: 8px 12px;
  background-color: transparent;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.back-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fde047;
}

.page-title {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #0f172a;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.badge-statement-date {
  font-size: 11px;
  font-weight: 700;
  background: #f8fafc;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 8px;
}

.refresh-icon-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
}

.btn-secondary {
  border: 1px solid #cbd5e1 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-gold-outline {
  border: 1px solid #9a6b27 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-primary {
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 4px 0 2px;
}

.kpi-value.gold-val {
  color: #9a6b27;
}

.unit-text {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11.5px;
  color: #64748b;
  display: flex;
  align-items: center;
}

.kpi-icon-pill {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Filter Card */
.filter-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;
}

.search-input-wrap {
  flex: 1;
  min-width: 240px;
  max-width: 320px;
}

.custom-search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.custom-search-field :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.custom-search-field :deep(input) {
  font-size: 13px !important;
}

.preset-btn {
  font-size: 11.5px !important;
  font-weight: 600 !important;
  text-transform: capitalize !important;
  border-radius: 6px !important;
  border: 1px solid #e2e8f0 !important;
  background: #f8fafc !important;
  color: #475569 !important;
}

.preset-active {
  background: #9a6b27 !important;
  color: #ffffff !important;
  border-color: #9a6b27 !important;
}

.date-input-field {
  width: 140px;
}

.date-input-field :deep(input) {
  font-size: 12px !important;
}

/* Tabs Bar */
.view-tabs-bar {
  padding: 0 4px;
}

.tab-toggle-btn {
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: capitalize !important;
  border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: #475569 !important;
}

.tab-active {
  background: #1e293b !important;
  color: #ffffff !important;
  border-color: #1e293b !important;
}

.statement-date-tag {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
}

/* Table Section Card */
.table-section-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.section-header-bar {
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.section-icon-box {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inward-bg {
  background: #dcfce7;
}

.outward-bg {
  background: #fef3c7;
}

.section-title {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.count-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 4px;
}

.inward-pill {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.outward-pill {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}

/* Table Styling */
.pro-statement-table :deep(th) {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.pro-statement-table :deep(td) {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 10px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.pro-statement-table :deep(tr:hover) {
  background-color: #fafbfc !important;
}

.bill-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.issue-badge {
  background: #fefce8;
  color: #b45309;
  border-color: #fef08a;
}

.date-text {
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
}

.less-badge {
  font-size: 11.5px;
  color: #b45309;
  background: #fffbeb;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #fef3c7;
}

.net-weight-badge {
  font-size: 12.5px;
  color: #92400e;
  background: #fefce8;
  padding: 2px 7px;
  border-radius: 6px;
  border: 1px solid #fef08a;
}

.amount-text {
  font-size: 13px;
}

/* Total Row */
.table-total-row td {
  background: #f8fafc !important;
  border-top: 2px solid #cbd5e1 !important;
  border-bottom: none !important;
  font-size: 13px !important;
}

.text-gold-total {
  color: #9a6b27 !important;
  font-size: 13.5px !important;
}

.text-emerald-total {
  color: #059669 !important;
  font-size: 13.5px !important;
}

.text-amber-total {
  color: #b45309 !important;
  font-size: 13.5px !important;
}

/* Reconciliation Summary Banner Card */
.reconciliation-summary-card {
  background: #ffffff !important;
  border: 1px solid #bfdbfe;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.04) !important;
}

.recon-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bfdbfe;
}

.recon-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.recon-sub {
  font-size: 11.5px;
  color: #64748b;
}

.recon-stat {
  display: flex;
  flex-direction: column;
}

.recon-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
}

.recon-val {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
}

/* Empty Table */
.empty-table-state {
  color: #64748b;
}

.empty-table-text {
  font-size: 12.5px;
  color: #64748b;
}

/* Utilities */
.text-emerald {
  color: #059669;
}

.text-amber {
  color: #b45309;
}

.text-slate-700 {
  color: #334155;
}

.text-slate-800 {
  color: #1e293b;
}

.text-muted {
  color: #94a3b8;
}
</style>
